import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Modal from "react-modal";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { FaUserPlus, FaEdit, FaUpload, FaSearch, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { gradeOptions, gradeTabs, filterStudentsFromGrade } from "../../constants/gradeOptions";

Modal.setAppElement("#root");

// 学生パスワードのフォーマットチェック関数
const isValidStudentPassword = (password) => {
    return password.length >= 8 && /[A-Za-z]/.test(password);
};

// 生徒IDを正規化する関数（前後の空白除去＆小文字変換）
const normalizeStudentId = (id) => {
    return id ? id.toString().trim().toLowerCase() : "";
};

// メール形式チェック関数（入力があればチェック）
const isValidEmail = (str) => /\S+@\S+\.\S+/.test(str);

const AdminStudents = () => {
    const [students, setStudents] = useState([]);
    const [withdrawnStudents, setWithdrawnStudents] = useState([]);
    const [showWithdrawn, setShowWithdrawn] = useState(false);
    const [formData, setFormData] = useState({
        studentid: "",
        name: "",
        password: "",
        withdrawn: false,
        parentid: "",
        grade: 1,
    });

    const [selectedGrade, setSelectedGrade] = useState("all");

    const [editingStudent, setEditingStudent] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // 保護者選択関連
    const [selectedParent, setSelectedParent] = useState(null);
    const [parentModalOpen, setParentModalOpen] = useState(false);
    const [parentList, setParentList] = useState([]);

    // ▼▼▼ 一括登録用 ▼▼▼
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [excelData, setExcelData] = useState([]);
    const [fileError, setFileError] = useState("");
    const [validationErrors, setValidationErrors] = useState([]);

    // フォームエラー用
    const [formError, setFormError] = useState("");
    // ローディング状態
    const [isLoading, setIsLoading] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkElapsedTime, setBulkElapsedTime] = useState(0);
    const [bulkTimer, setBulkTimer] = useState(null);

    const filteredStudents = filterStudentsFromGrade(students, selectedGrade);

    const MIN_GRADE = 1;
    const MAX_GRADE = 13;

    // ========================
    //  学生一覧の取得（現役学生のみ、studentid昇順）
    // ========================
    const fetchStudents = async () => {
        try {
            // 学生データ取得
            const data = await getRequest("students?excludeWithdrawn=true");
            let studentData = data && data.students ? data.students : [];
            // 管理画面専用：保護者一覧も取得してマージする
            const parentRes = await getRequest("parents");
            const parentList = parentRes && parentRes.parents ? parentRes.parents : [];
            const parentMap = {};
            parentList.forEach((parent) => {
                parentMap[parent.id] = parent;
            });
            studentData = studentData.map((student) => ({
                ...student,
                parent: student.parentid ? parentMap[student.parentid] : null,
            }));
            setStudents(studentData);
        } catch (error) {
            console.error("❌ 学生データ取得エラー:", error);
        }
    };

    // 退会済み学生一覧の取得
    const fetchWithdrawnStudents = async () => {
        try {
            // excludeWithdrawn が指定されていないので全学生を取得
            const data = await getRequest("students");
            let allStudents = data && data.students ? data.students : [];
            // 退会済みのみフィルタ
            const withdrawn = allStudents.filter(student => student.withdrawn);
            const parentRes = await getRequest("parents");
            const parentList = parentRes && parentRes.parents ? parentRes.parents : [];
            const parentMap = {};
            parentList.forEach((parent) => {
                parentMap[parent.id] = parent;
            });
            const withdrawnData = withdrawn.map(student => ({
                ...student,
                parent: student.parentid ? parentMap[student.parentid] : null,
            }));
            setWithdrawnStudents(withdrawnData);
        } catch (error) {
            console.error("❌ 退会済み学生取得エラー:", error);
        }
    };

    useEffect(() => {
        fetchStudents();
    }, []);

    // ========================
    //  個別登録／更新フォーム処理
    // ========================
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // 保護者一覧モーダルを開く
    const openParentModal = async () => {
        try {
            const data = await getRequest("parents");
            setParentList(data.parents || []);
            setParentModalOpen(true);
        } catch (err) {
            console.error("保護者一覧取得エラー:", err);
            toast.error("保護者一覧の取得に失敗しました");
        }
    };

    const closeParentModal = () => {
        setParentModalOpen(false);
    };

    // 保護者選択時の処理
    const handleSelectParent = (parent) => {
        setSelectedParent(parent);
        setFormData({ ...formData, parentid: parent.id });
        closeParentModal();
    };

    // 個別登録（学生追加）
    const addStudent = async () => {
        setIsLoading(true);
        if (!formData.studentid || !formData.name || !formData.password) {
            setFormError("学生ID、氏名、パスワードは必須です");
            setIsLoading(false);
            return;
        }
        // studentidの重複チェック
        if (students.find((s) => s.studentid === formData.studentid)) {
            setFormError("入力された学生IDは既に使用されています");
            setIsLoading(false);
            return;
        }
        if (!isValidStudentPassword(formData.password)) {
            setFormError("パスワードは8文字以上で、英字を含む必要があります");
            setIsLoading(false);
            return;
        }
        if (!formData.parentid) {
            setFormError("保護者が選択されていません");
            setIsLoading(false);
            return;
        }
        try {
            const response = await postRequest("students", formData);
            if (response.ok) {
                fetchStudents();
                setFormError("");
                // 登録後はフォームの内容をクリア
                setFormData({
                    studentid: "",
                    name: "",
                    password: "",
                    withdrawn: false,
                    parentid: "",
                    grade: 1,
                });
                setSelectedParent(null);
                toast.success("学生登録が完了しました");
            } else {
                const data = await response.json();
                setFormError(data.error || "学生追加に失敗しました");
            }
        } catch (error) {
            console.error("❌ 学生追加エラー:", error);
            setFormError("学生追加の通信に失敗しました");
        } finally {
            setIsLoading(false);
        }
    };

    // 編集開始時の処理
    const startEditing = (student) => {
        setEditingStudent(student.id);
        setFormError("");
        setFormData({
            studentid: student.studentid,
            name: student.name,
            password: "",
            withdrawn: student.withdrawn,
            parentid: student.parentid || "",
            grade: student.grade,  // ← ここで学年をセット
        });
        setSelectedParent(student.parent || null);
        setIsEditing(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // 新規追加モードでフォームを開くための処理
    const openNewForm = () => {
        setEditingStudent(null);
        setFormError("");
        setFormData({
            studentid: "",
            name: "",
            password: "",
            withdrawn: false,
            parentid: "",
            grade: 1,
        });
        setSelectedParent(null);
        setIsEditing(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const updateStudent = async () => {
        setIsLoading(true);
        if (!formData.studentid || !formData.name) {
            setFormError("学生ID、氏名は必須です");
            setIsLoading(false);
            return;
        }
        // 更新時：対象以外でstudentid重複チェック
        if (students.find((s) => s.studentid === formData.studentid && s.id !== editingStudent)) {
            setFormError("入力された学生IDは既に使用されています");
            setIsLoading(false);
            return;
        }
        if (!formData.parentid) {
            setFormError("保護者が選択されていません");
            setIsLoading(false);
            return;
        }
        try {
            const response = await putRequest(`students/${editingStudent}`, formData);
            if (response.ok) {
                fetchStudents();
                setFormError("");
                setEditingStudent(null);
                setFormData({
                    studentid: "",
                    name: "",
                    password: "",
                    withdrawn: false,
                    parentid: "",
                    grade: 1,
                });
                setSelectedParent(null);
                toast.success("学生情報の更新が完了しました");
            } else {
                const data = await response.json();
                setFormError(data.error || "学生情報の更新に失敗しました");
            }
        } catch (error) {
            console.error("❌ 学生更新エラー:", error);
            setFormError("学生情報更新の通信に失敗しました");
        } finally {
            setIsLoading(false);
        }
    };

    const cancelEditing = () => {
        setEditingStudent(null);
        setIsEditing(false);
        setFormError("");
        setFormData({
            studentid: "",
            name: "",
            password: "",
            withdrawn: false,
            parentid: "",
            grade: 1,
        });
        setSelectedParent(null);
    };

    const retireStudent = async (id) => {
        try {
            const response = await putRequest(`students/${id}`, { withdrawn: true });
            if (response.ok) {
                fetchStudents();
                fetchWithdrawnStudents();
                toast.success("学生の退会処理が完了しました");
            }
        } catch (error) {
            console.error("❌ 退会処理エラー:", error);
        }
    };

    // 新たに生徒削除処理（deleteRequest を利用）
    const deleteStudent = async (id) => {
        try {
            const response = await deleteRequest(`students/${id}`);
            if (response.ok) {
                fetchStudents();
                fetchWithdrawnStudents();
                toast.success("学生が削除されました");
            } else {
                if (response.status == 403) {
                    toast.error("この生徒の授業報告書が存在するため、削除できません");
                } else {
                    toast.error("学生削除に失敗しました");
                }
            }
        } catch (error) {
            console.error("❌ 学生削除エラー:", error);
            toast.error("学生削除の通信に失敗しました");
        }
    };

    // 退会済み学生の表示切替
    const toggleWithdrawn = () => {
        if (!showWithdrawn) {
            fetchWithdrawnStudents();
        }
        setShowWithdrawn(!showWithdrawn);
    };


    // ========================
    //  一括登録モーダル関連（Excelアップロード）
    // ========================
    const openBulkModal = () => {
        setBulkModalOpen(true);
        setExcelData([]);
        setFileError("");
        setValidationErrors([]);
    };

    const closeBulkModal = () => {
        if (!bulkLoading) {
            setBulkModalOpen(false);
            setExcelData([]);
            setFileError("");
            setValidationErrors([]);
        }
    };

    // 生徒名から「苗字様（名前）」形式の保護者名を生成する関数
    const generateParentName = (studentName) => {
        if (!studentName) return "保護者";
        const parts = studentName.split(" ");
        if (parts.length >= 2) {
            return `${parts[0]}様（${parts.slice(1).join(" ")}）`;
        }
        return `${studentName}様`;
    };

    // 学年の変換関数（"大学浪人" は "既卒" として数値13に変換）
    const convertGradeString = (gradeStr) => {
        if (!gradeStr) return null;
        gradeStr = gradeStr.trim();
        const mapping = {
            "小学校1年": 1,
            "小学校2年": 2,
            "小学校3年": 3,
            "小学校4年": 4,
            "小学校5年": 5,
            "小学校6年": 6,
            "中学校1年": 7,
            "中学校2年": 8,
            "中学校3年": 9,
            "高校1年": 10,
            "高校2年": 11,
            "高校3年": 12,
            "大学浪人": 13,
            "既卒": 13,
        };
        return mapping[gradeStr] !== undefined ? mapping[gradeStr] : null;
    };

    // 数値の学年を対応するラベルに変換する関数
    const getGradeLabel = (grade) => {
        const option = gradeTabs.find((opt) => Number(opt.value) === Number(grade));
        return option ? option.label : "";
    };

    // Excel のフォーマット処理
    const handleExcelFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.name.endsWith(".xls") && !file.name.endsWith(".xlsx")) {
            setFileError("Excelファイル（.xls, .xlsx）を選択してください");
            return;
        }
        setFileError("");
        setValidationErrors([]);
        try {
            const reader = new FileReader();
            reader.onload = async (evt) => {
                const bstr = evt.target.result;
                const workbook = XLSX.read(bstr, { type: "binary" });
                const sheetName = workbook.SheetNames[0];
                const ws = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });
                // 各行のデータに対して、生成用と表示用の保護者名を分けて保持
                const dataRows = jsonData.slice(4).map((row, i) => {
                    const studentName = row[6] || "";
                    const rawParentPhone = row[13] ? row[13].toString() : "";
                    const cleanedParentPhone = rawParentPhone.replace(/-/g, "");
                    const generatedParentName = generateParentName(studentName);
                    return {
                        rowIndex: i + 5,
                        studentid: row[5] || "",
                        name: studentName,
                        password: row[32] || "",
                        parentPhone: cleanedParentPhone,
                        parentEmail: row[15] || "",
                        parentName: generatedParentName,
                        // 初期値は生成された名前（後で、既存があれば上書き）
                        displayParentName: generatedParentName,
                        // 学年はK列（インデックス10）の文字列を変換
                        grade: row[10] ? convertGradeString(row[10]) : "",
                    };
                });

                // 既存の保護者一覧を取得し、電話番号をキーにマップを作成
                const parentRes = await getRequest("parents");
                const existingParents = parentRes && parentRes.parents ? parentRes.parents : [];
                const parentMap = {};
                existingParents.forEach((parent) => {
                    const phone = parent.phonenumber.replace(/-/g, "");
                    parentMap[phone] = parent.name;
                });

                // 各行について、parentPhone がマップにあれば表示用保護者名を上書き、なければ「新規」
                dataRows.forEach((row) => {
                    if (row.parentPhone && parentMap[row.parentPhone]) {
                        row.displayParentName = parentMap[row.parentPhone];
                    } else {
                        row.displayParentName = "新規";
                    }
                });
                setExcelData(dataRows);

                // バリデーション処理（ここは従来通り）
                const errors = [];
                dataRows.forEach((row) => {
                    let rowErrors = [];
                    if (!row.studentid) rowErrors.push("学生IDが空です");
                    if (!row.name) rowErrors.push("名前が空です");
                    if (!row.password) {
                        rowErrors.push("パスワードが空です");
                    } else if (row.password.length < 8 || !/[A-Za-z]/.test(row.password)) {
                        rowErrors.push("パスワードは8文字以上で、英字を含む必要があります");
                    }
                    if (!row.parentPhone) {
                        rowErrors.push("保護者の電話が必要です");
                    }
                    if (row.parentPhone && !/^(070|080|090)\d{8}$/.test(row.parentPhone)) {
                        rowErrors.push("有効な携帯電話番号を入力してください");
                    }
                    // 学年のバリデーション：必須チェックと範囲チェック
                    if (row.grade === "" || row.grade === null) {
                        rowErrors.push("学年が空です");
                    } else if (row.grade < MIN_GRADE || row.grade > MAX_GRADE) {
                        rowErrors.push(`学年は${MIN_GRADE}～${MAX_GRADE}の範囲で指定してください`);
                    }
                    if (rowErrors.length > 0) {
                        errors.push({ rowIndex: row.rowIndex, messages: rowErrors });
                    }
                });
                setValidationErrors(errors);
            };
            reader.readAsBinaryString(file);
        } catch (err) {
            console.error("❌ Excel解析エラー:", err);
            setFileError("Excelファイルの解析に失敗しました");
        }
    };


    const handleBulkRegister = async () => {
        if (excelData.length === 0) {
            setFileError("アップロードされたデータがありません");
            return;
        }
        if (validationErrors.length > 0) {
            setFileError("データに不備があります。修正してから再度アップロードしてください。");
            return;
        }

        // 既存の生徒ID一覧を取得
        const existingStudentIds = students.map((s) => s.studentid);
        // Excelの各行の生徒IDが既に登録されていないかチェックしてフィルタ
        const filteredExcelData = excelData.filter((row) => !existingStudentIds.includes(row.studentid));

        // 重複していた件数がある場合にお知らせする
        if (filteredExcelData.length < excelData.length) {
            toast.info(`重複している ${excelData.length - filteredExcelData.length} 件の生徒は省かれました`);
        }

        if (filteredExcelData.length === 0) {
            setFileError("アップロードされたデータは全て既に登録されています");
            return;
        }

        setBulkLoading(true);
        setBulkElapsedTime(0);
        const timer = setInterval(() => {
            setBulkElapsedTime((prev) => prev + 1);
        }, 1000);
        setBulkTimer(timer);
        try {
            const response = await postRequest("students/bulk", { students: filteredExcelData });
            closeBulkModal();
            if (response.ok) {
                fetchStudents();
                toast.success("一括登録が完了しました");
            } else {
                const data = await response.json();
                console.error("❌ バルク登録APIエラー:", data);
                toast.error(data.error || "一括登録に失敗しました");
            }
        } catch (error) {
            console.error("❌ 一括登録エラー:", error);
            toast.error("一括登録の通信に失敗しました");
            closeBulkModal();
        } finally {
            clearInterval(timer);
            setBulkTimer(null);
            setBulkLoading(false);
        }
    };


    // 新規保護者登録用の状態
    const [newParentModalOpen, setNewParentModalOpen] = useState(false);
    const [newParentData, setNewParentData] = useState({
        name: "",
        phonenumber: "",
        email: "",
    });
    const [newParentError, setNewParentError] = useState("");

    // 新規保護者登録の入力変更ハンドラ
    const handleNewParentChange = (e) => {
        setNewParentData({ ...newParentData, [e.target.name]: e.target.value });
    };

    // 新規保護者登録の送信処理
    const handleNewParentSubmit = async () => {
        if (!newParentData.name || !newParentData.phonenumber) {
            setNewParentError("名前と電話番号は必須です");
            return;
        }
        const cleanedPhone = newParentData.phonenumber.replace(/-/g, "");
        if (!/^(070|080|090)\d{8}$/.test(cleanedPhone)) {
            setNewParentError("有効な携帯電話番号を入力してください");
            return;
        }
        // ここで、メールが入力されている場合のみ形式チェックを実施
        if (newParentData.email && !isValidEmail(newParentData.email)) {
            setNewParentError("メールアドレスの形式が不正です");
            return;
        }
        const payload = { ...newParentData, phonenumber: cleanedPhone };
        try {
            const response = await postRequest("parents", payload);
            if (response.ok) {
                const resData = await response.json();
                const createdParent = resData.parent;
                setSelectedParent(createdParent);
                setFormData({ ...formData, parentid: createdParent.id });
                toast.success("保護者登録が完了しました");
                setNewParentModalOpen(false);
                setParentModalOpen(false);
            } else {
                const data = await response.json();
                setNewParentError(data.error || "保護者登録に失敗しました");
            }
        } catch (error) {
            console.error("保護者登録エラー:", error);
            setNewParentError("保護者登録の通信に失敗しました");
        }
    };

    // 携帯電話番号フォーマット関数
    const formatMobilePhone = (phone) => {
        if (!phone) return "";
        const cleaned = phone.replace(/-/g, "");
        if (/^(070|080|090)\d{8}$/.test(cleaned)) {
            return cleaned.slice(0, 3) + "-" + cleaned.slice(3, 7) + "-" + cleaned.slice(7);
        }
        return phone;
    };

    return (
        <div className="p-6 pt-20 bg-gray-100 min-h-screen">
            <h1 className="text-2xl font-bold mb-4">学生管理</h1>

            {/* 一括登録、新規追加ボタン */}
            <div className="flex gap-4 mb-6">
                <button
                    onClick={openBulkModal}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
                >
                    <FaUpload className="inline mr-2" />
                    一括登録
                </button>
                <button
                    onClick={openNewForm}
                    className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                >
                    <FaUserPlus className="inline mr-2" />
                    新規追加
                </button>
            </div>

            {/* 個別登録／更新フォーム */}
            {isEditing && (
                <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold mb-3">
                            {editingStudent ? "学生を編集" : "新しい学生を追加"}
                        </h2>
                        <button
                            onClick={cancelEditing}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <FaTimes size={20} />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <input
                            type="text"
                            name="studentid"
                            value={formData.studentid}
                            onChange={handleChange}
                            placeholder="学生ID"
                            className="p-2 border rounded"
                        />
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="氏名"
                            className="p-2 border rounded"
                        />
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="パスワード（更新時は任意）"
                            className="p-2 border rounded"
                        />
                        {/* 学年選択 */}
                        <select
                            name="grade"
                            value={formData.grade}
                            onChange={handleChange}
                            className="p-2 border rounded"
                        >
                            {gradeOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        {/* 保護者選択UIはそのまま */}
                        <div className="flex flex-col">
                            <button
                                onClick={openParentModal}
                                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition mb-2"
                            >
                                <FaSearch className="inline mr-2" />
                                保護者一覧を表示
                            </button>
                            {selectedParent ? (
                                <div className="border p-2 rounded">
                                    <div>名前: {selectedParent.name}</div>
                                    <div>電話: {selectedParent.phonenumber}</div>
                                    <div>メール: {selectedParent.email || "-"}</div>
                                </div>
                            ) : (
                                <div className="text-gray-500">保護者が未選択です</div>
                            )}
                        </div>
                    </div>
                    {formError && <p className="text-red-500 mt-2">{formError}</p>}
                    <div className="flex gap-4 mt-3">
                        {editingStudent ? (
                            <>
                                <button
                                    onClick={updateStudent}
                                    disabled={isLoading}
                                    className={`px-4 py-2 rounded-lg text-white transition ${isLoading ? "bg-blue-300" : "bg-blue-500 hover:bg-blue-600"}`}
                                >
                                    {isLoading ? "更新中…" : "更新"}
                                </button>
                                <button
                                    onClick={cancelEditing}
                                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition"
                                >
                                    キャンセル
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => {
                                    if (!formData.parentid) {
                                        setFormError("保護者が選択されていません");
                                        return;
                                    }
                                    addStudent();
                                }}
                                disabled={isLoading}
                                className={`px-4 py-2 rounded-lg text-white transition ${isLoading ? "bg-green-300" : "bg-green-500 hover:bg-green-600"}`}
                            >
                                {isLoading ? "追加中…" : (<><FaUserPlus className="inline mr-2" />追加</>)}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* 学生一覧 */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <h2 className="text-lg font-semibold mb-3">現役学生一覧</h2>
                {/* 学年フィルタ（PC: タブ / モバイル: ドロップダウン） */}
                <div className="mb-4">
                    <div className="hidden md:flex space-x-2 overflow-x-auto pb-2">
                        {gradeTabs.map((opt) => (
                            <button
                                key={opt.value}
                                className={`px-4 py-2 rounded-lg transition ${selectedGrade === opt.value ? "bg-blue-500 text-white" : "bg-gray-200"}`}
                                onClick={() => setSelectedGrade(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <div className="md:hidden">
                        <select
                            value={selectedGrade}
                            onChange={(e) => setSelectedGrade(e.target.value)}
                            className="w-full p-2 border rounded"
                        >
                            {gradeOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-gray-200">
                            <th className="p-2 border">学生ID</th>
                            <th className="p-2 border">氏名</th>
                            <th className="p-2 border">学年</th>
                            <th className="p-2 border">保護者情報</th>
                            <th className="p-2 border">パスワード</th>
                            <th className="p-2 border">状態</th>
                            <th className="p-2 border">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStudents.map((student) => (
                            <tr key={student.id} className="border">
                                <td className="p-2 border">{student.studentid}</td>
                                <td className="p-2 border">{student.name}</td>
                                <td className="p-2 border">{getGradeLabel(student.grade)}</td>
                                <td className="p-2 border">
                                    {student.parent
                                        ? `${student.parent.name} (${formatMobilePhone(student.parent.phonenumber)})`
                                        : "-"}
                                </td>
                                <td className="p-2 border">
                                    {student.plainPassword ? student.plainPassword : "-"}
                                </td>
                                <td className="p-2 border">
                                    {student.withdrawn ? "退会済" : "現役"}
                                </td>
                                <td className="p-2 border flex gap-2">
                                    <button
                                        onClick={() => startEditing(student)}
                                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (window.confirm(`本当に「${student.name}」さんを退会にしますか？`)) {
                                                retireStudent(student.id);
                                            }
                                        }}
                                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                                    >
                                        退会
                                    </button>

                                    {/* 削除ボタンを常時表示 */}
                                    <button
                                        onClick={() => {
                                            if (window.confirm("本当に削除しますか？")) {
                                                deleteStudent(student.id);
                                            }
                                        }}
                                        className="bg-red-700 text-white px-3 py-1 rounded hover:bg-red-800"
                                    >
                                        削除
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 退会済み学生一覧（デフォルトは非表示、ボタンで展開） */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <button
                    onClick={toggleWithdrawn}
                    className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition mb-4"
                >
                    {showWithdrawn ? "退会済み学生を非表示" : "退会済み学生を表示"}
                </button>
                {showWithdrawn && (
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-gray-200">
                                <th className="p-2 border">学生ID</th>
                                <th className="p-2 border">氏名</th>
                                <th className="p-2 border">保護者情報</th>
                                <th className="p-2 border">パスワード</th>
                                <th className="p-2 border">状態</th>
                                <th className="p-2 border">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {withdrawnStudents.map((student) => (
                                <tr key={student.id} className="border">
                                    <td className="p-2 border">{student.studentid}</td>
                                    <td className="p-2 border">{student.name}</td>
                                    <td className="p-2 border">
                                        {student.parent
                                            ? `${student.parent.name} (${formatMobilePhone(student.parent.phonenumber)})`
                                            : "-"}
                                    </td>
                                    <td className="p-2 border">{student.plainPassword ? student.plainPassword : "-"}</td>
                                    <td className="p-2 border">退会済</td>
                                    <td className="p-2 border flex gap-2">
                                        <button
                                            onClick={() => startEditing(student)}
                                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                                        >
                                            <FaEdit />
                                        </button>
                                        {/* 削除ボタンを常時表示 */}
                                        <button
                                            onClick={() => {
                                                if (window.confirm("本当に削除しますか？")) {
                                                    deleteStudent(student.id);
                                                }
                                            }}
                                            className="bg-red-700 text-white px-3 py-1 rounded hover:bg-red-800"
                                        >
                                            削除
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {/* 一括登録モーダル */}
            <Modal
                isOpen={bulkModalOpen}
                onRequestClose={closeBulkModal}
                contentLabel="一括登録モーダル"
                className="bg-white p-4 rounded-lg shadow-md max-w-3xl mx-auto my-20"
                overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center"
            >
                <h2 className="text-xl font-semibold mb-4">学生一括登録</h2>
                <input
                    type="file"
                    accept=".xls,.xlsx"
                    onChange={handleExcelFile}
                    className="p-2 border rounded"
                />
                {fileError && <p className="text-red-500 mt-2">{fileError}</p>}
                {excelData.length > 0 && (
                    <div className="mt-4 max-h-80 overflow-y-auto">
                        {/*
          既存の生徒ID一覧を正規化して取得
          ※studentsはfetchStudentsで取得済みのデータ
        */}
                        {(() => {
                            const normalizedExistingIds = students.map((s) => normalizeStudentId(s.studentid));
                            return (
                                <table className="w-full border-collapse text-sm">
                                    <thead>
                                        <tr className="bg-gray-200">
                                            <th className="p-2 border">行番号</th>
                                            <th className="p-2 border">学生ID</th>
                                            <th className="p-2 border">氏名</th>
                                            <th className="p-2 border">学年</th>
                                            <th className="p-2 border">保護者</th>
                                            <th className="p-2 border">パスワード</th>
                                            <th className="p-2 border">保護者電話</th>
                                            <th className="p-2 border">保護者メール</th>
                                            <th className="p-2 border">エラー</th>
                                            <th className="p-2 border">登録状態</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {excelData.map((row, idx) => {
                                            const rowErr = validationErrors.find((err) => err.rowIndex === row.rowIndex);
                                            const isDuplicate = normalizedExistingIds.includes(normalizeStudentId(row.studentid));
                                            return (
                                                <tr key={idx} className={rowErr ? "border bg-red-50" : "border bg-white"}>
                                                    <td className="p-2 border">{row.rowIndex}</td>
                                                    <td className="p-2 border">{row.studentid}</td>
                                                    <td className="p-2 border">{row.name}</td>
                                                    <td className="p-2 border">{getGradeLabel(row.grade)}</td>
                                                    <td className="p-2 border">{row.displayParentName}</td>
                                                    <td className="p-2 border">{row.password}</td>
                                                    <td className="p-2 border">{row.parentPhone || "-"}</td>
                                                    <td className="p-2 border">{row.parentEmail || "-"}</td>
                                                    <td className="p-2 border text-xs text-red-600">
                                                        {rowErr ? rowErr.messages.map((m, i) => <div key={i}>{m}</div>) : null}
                                                    </td>
                                                    <td className="p-2 border">
                                                        {isDuplicate ? (
                                                            <span className="text-red-600 font-semibold">既に登録済み</span>
                                                        ) : (
                                                            <span className="text-green-600">登録可能</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            );
                        })()}
                    </div>
                )}
                {bulkLoading && (
                    <div className="mt-4 text-blue-600 font-semibold">
                        登録中… 経過時間: {bulkElapsedTime}秒
                    </div>
                )}
                <div className="mt-4 flex gap-4">
                    <button
                        onClick={handleBulkRegister}
                        disabled={bulkLoading}
                        className={`bg-green-500 text-white px-4 py-2 rounded-lg transition ${bulkLoading ? "bg-green-300" : "hover:bg-green-600"}`}
                    >
                        {bulkLoading ? "登録中…" : "登録実行"}
                    </button>
                    <button
                        onClick={closeBulkModal}
                        disabled={bulkLoading}
                        className={`bg-gray-500 text-white px-4 py-2 rounded-lg transition ${bulkLoading ? "bg-gray-300" : "hover:bg-gray-600"}`}
                    >
                        キャンセル
                    </button>
                </div>
            </Modal>

            {/* 保護者一覧モーダル */}
            <Modal
                isOpen={parentModalOpen}
                onRequestClose={closeParentModal}
                contentLabel="保護者一覧モーダル"
                className="bg-white p-4 rounded-lg shadow-md max-w-md mx-auto my-20"
                overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center"
            >
                <h2 className="text-xl font-semibold mb-4">保護者一覧</h2>
                <div className="max-h-64 overflow-y-auto">
                    {parentList.length > 0 ? (
                        parentList.map((parent) => (
                            <div
                                key={parent.id}
                                className="border p-2 rounded mb-2 cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSelectParent(parent)}
                            >
                                <div>名前: {parent.name}</div>
                                <div>電話: {parent.phonenumber}</div>
                                <div>メール: {parent.email || "-"}</div>
                            </div>
                        ))
                    ) : (
                        <div className="text-gray-500">保護者が登録されていません</div>
                    )}
                </div>
                {/* 新規保護者登録ボタンの追加 */}
                <button
                    onClick={() => {
                        closeParentModal();
                        setNewParentModalOpen(true);
                    }}
                    className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition w-full mt-4"
                >
                    新規保護者登録
                </button>
                <button
                    onClick={closeParentModal}
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition mt-4 w-full"
                >
                    キャンセル
                </button>
            </Modal>
            {/* 新規保護者登録モーダル */}
            <Modal
                isOpen={newParentModalOpen}
                onRequestClose={() => setNewParentModalOpen(false)}
                contentLabel="新規保護者登録モーダル"
                className="bg-white p-4 rounded-lg shadow-md max-w-md mx-auto my-20"
                overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center"
            >
                <h2 className="text-xl font-semibold mb-4">新規保護者登録</h2>
                <div className="flex flex-col gap-4">
                    <input
                        type="text"
                        name="name"
                        value={newParentData.name}
                        onChange={handleNewParentChange}
                        placeholder="保護者名"
                        className="p-2 border rounded"
                    />
                    <input
                        type="text"
                        name="phonenumber"
                        value={newParentData.phonenumber}
                        onChange={handleNewParentChange}
                        placeholder="電話番号"
                        className="p-2 border rounded"
                    />
                    <input
                        type="email"
                        name="email"
                        value={newParentData.email}
                        onChange={handleNewParentChange}
                        placeholder="メール（任意）"
                        className="p-2 border rounded"
                    />
                </div>
                {newParentError && <p className="text-red-500 mt-2">{newParentError}</p>}
                <div className="flex gap-4 mt-4">
                    <button
                        onClick={handleNewParentSubmit}
                        className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition w-full"
                    >
                        登録
                    </button>
                    <button
                        onClick={() => setNewParentModalOpen(false)}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition w-full"
                    >
                        キャンセル
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default AdminStudents;
